import {useEffect, useRef, useSyncExternalStore} from 'react'

// Optimistic task title overrides for the agents sidebar. The server refetch
// still owns the canonical value; the override just shows the rename right away.

interface Override {
  optimistic: string
  // Sources we've seen or superseded. Server echoes of any of them are
  // ignored; anything else is treated as a remote rename and yields.
  pendingSources: readonly string[]
}

const overrides = new Map<string, Override>()
const listeners = new Set<() => void>()

function notify(): void {
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function sameSources(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}

export function setTaskTitleOverride(taskId: string, optimistic: string, sourceTitle: string): void {
  const trimmed = optimistic.trim()
  if (!trimmed) return
  const previous = overrides.get(taskId)
  // Callers may still see the pre-rename source when a follow-up rename fires
  // before the server catches up. Roll the previous optimistic into the set
  // so its stale echo does not clear this newer override.
  const pendingSources = previous
    ? Array.from(new Set([...previous.pendingSources, previous.optimistic, sourceTitle]))
    : [sourceTitle]
  if (previous && previous.optimistic === trimmed && sameSources(previous.pendingSources, pendingSources)) return
  overrides.set(taskId, {optimistic: trimmed, pendingSources})
  notify()
}

export function clearTaskTitleOverride(taskId: string): void {
  if (!overrides.delete(taskId)) return
  notify()
}

export function useResolvedTaskTitle(taskId: string | undefined, sourceTitle: string): string {
  const entry = useSyncExternalStore(
    subscribe,
    () => (taskId ? overrides.get(taskId) : undefined),
    () => undefined,
  )
  // Track the last source we acted on so we only clear on a real transition.
  // Equality alone is ambiguous: on A → B → A the latest optimistic equals the
  // still-stale source, and clearing there would drop the newest override
  // before the server has acknowledged anything.
  const lastSourceRef = useRef(sourceTitle)

  useEffect(() => {
    const previousSource = lastSourceRef.current
    lastSourceRef.current = sourceTitle
    if (!taskId || !entry) return
    if (sourceTitle === previousSource) return
    if (sourceTitle === entry.optimistic) {
      clearTaskTitleOverride(taskId)
      return
    }
    if (!entry.pendingSources.includes(sourceTitle)) {
      clearTaskTitleOverride(taskId)
    }
  }, [taskId, sourceTitle, entry])

  return entry?.optimistic ?? sourceTitle
}
